import React, { memo } from "react";
import { Input } from "components/common/input";
import { TextArea } from "components/common/textArea/TextArea";
import { Controller, useFormContext } from "react-hook-form";
import { FieldGridProps } from "./FieldGrid.types";

const FieldGrid = memo(
  <T extends Record<string, any>>({ configs, item: _item, className }: FieldGridProps<T>) => {
    const { control } = useFormContext();

    // Render fields directly without memoization to avoid infinite re-renders
    const renderedFields = configs.map((cfg) => {
      const key = cfg.key;

      // Always in edit mode: use custom component or fallback to Input/TextArea
      if (cfg.EditComponent) {
        return <React.Fragment key={key}>{cfg.EditComponent}</React.Fragment>;
      }

      // Use TextArea for textarea type
      if (cfg.type === "textarea") {
        return (
          <div className="mb-4" key={key}>
            <Controller
              name={cfg.name!}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextArea
                  {...field}
                  disabled={false}
                  rows={4}
                  label={cfg.label} // Pass label to TextArea
                  placeholder={`Enter ${cfg.label.toLowerCase()}...`}
                  alert={error ? { type: "error", message: error.message || "" } : undefined}
                />
              )}
            />
          </div>
        );
      }

      // All other types: use Input wrapped in Controller
      return (
        <div className="mb-4" key={key}>
          <Controller
            name={cfg.name!}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                label={cfg.label}
                type={cfg.type!}
                disabled={false}
                size="sm"
                showError={!!error}
              />
            )}
          />
        </div>
      );
    });

    return <div className={`grid ${className ?? "grid-cols-2"} gap-4`}>{renderedFields}</div>;
  }
) as <T extends Record<string, any>>(props: FieldGridProps<T>) => React.ReactElement;

(FieldGrid as any).displayName = "FieldGrid";

export default FieldGrid;
